import type {
  DomElementPosition,
  DomOffScreenLocation,
  ElementAbsoluteLocation,
  ElementSize,
  WindowSize,
} from './common-interfaces';
import { DomElementAlignment, DomTargetPosition, ScreenPosition } from './enums';

export class DomHandler {
  public static positionToTarget(
    target: HTMLElement,
    position: DomTargetPosition = DomTargetPosition.BottomLeft
  ): ElementAbsoluteLocation {
    const rectangle = target.getBoundingClientRect();

    let top = 0;
    let left = 0;
    const scrollTop = window.scrollY;

    switch (position) {
      case DomTargetPosition.BottomLeft:
        top = rectangle.top + rectangle.height + scrollTop;
        left = rectangle.left;
        break;
      case DomTargetPosition.TopLeft:
        top = rectangle.top + scrollTop;
        left = rectangle.left;
        break;
      case DomTargetPosition.BottomRight:
        top = rectangle.top + rectangle.height + scrollTop;
        left = rectangle.left + rectangle.width;
        break;
      case DomTargetPosition.TopRight:
        top = rectangle.top + scrollTop;
        left = rectangle.left + rectangle.width;
        break;
      case DomTargetPosition.Left:
        top = rectangle.top + rectangle.height / 2 + scrollTop;
        left = rectangle.left;
        break;
      case DomTargetPosition.Right:
        top = rectangle.top + rectangle.height / 2 + scrollTop;
        left = rectangle.left + rectangle.width;
        break;
    }

    return {
      top,
      left,
    };
  }

  public static positionElementToTarget(
    element: HTMLElement,
    target: HTMLElement,
    elementPosition: DomTargetPosition = DomTargetPosition.BottomLeft,
    elementAlignment: DomElementAlignment = DomElementAlignment.TopLeft
  ): ElementAbsoluteLocation {
    let { left, top } = DomHandler.positionToTarget(target, elementPosition);
    const elementRectangle = element.getBoundingClientRect();

    switch (elementAlignment) {
      case DomElementAlignment.TopLeft:
        // top left is the default
        if (window.innerHeight < elementRectangle.bottom) {
          //decreese top attribute by a height of a hidden dialog's part
          top = top - (elementRectangle.bottom - window.innerHeight);
        }
        break;
      case DomElementAlignment.TopRight:
        left = Math.max(left - elementRectangle.width, 0);
        if (window.innerHeight < elementRectangle.bottom) {
          top = top - (elementRectangle.bottom - window.innerHeight);
        }
        break;
      case DomElementAlignment.BottomLeft:
        top = top - elementRectangle.height;
        break;
      case DomElementAlignment.BottomRight:
        top = top - elementRectangle.height;
        left = left - elementRectangle.width;
        break;
    }

    return {
      top,
      left,
    };
  }

  public static canPositionElementOnScreenWithTarget(element: HTMLElement, target: HTMLElement) {
    const screen = DomHandler.getScreenDimensions();
    const rectangle = element.getBoundingClientRect();

    const bottomLeft = DomHandler.positionElementToTarget(
      element,
      target,
      DomTargetPosition.BottomLeft,
      DomElementAlignment.TopLeft
    );
    if (bottomLeft.left + rectangle.width < screen.width && bottomLeft.top + rectangle.height < screen.height) {
      return true;
    }

    const bottomRight = DomHandler.positionElementToTarget(
      element,
      target,
      DomTargetPosition.BottomRight,
      DomElementAlignment.TopRight
    );
    if (bottomRight.left >= 0 && bottomLeft.top + rectangle.height < screen.height) {
      return true;
    }

    const topLeft = DomHandler.positionElementToTarget(
      element,
      target,
      DomTargetPosition.TopLeft,
      DomElementAlignment.BottomLeft
    );
    if (topLeft.left + rectangle.width < screen.width && topLeft.top >= 0 && rectangle.left >= 0) {
      return true;
    }

    const topRight = DomHandler.positionElementToTarget(
      element,
      target,
      DomTargetPosition.TopRight,
      DomElementAlignment.BottomRight
    );
    return topRight.left >= 0 && topRight.top >= 0;
  }

  public static canPositionElementOnScreen(element: HTMLElement) {
    const screen = DomHandler.getScreenDimensions();
    const rectangle = element.getBoundingClientRect();

    return rectangle.width <= screen.width && rectangle.height <= screen.height;
  }

  public static positionToTargetWithScroll(
    target: HTMLElement,
    position: DomTargetPosition = DomTargetPosition.BottomLeft
  ): ElementAbsoluteLocation {
    const targetPosition = DomHandler.positionToTarget(target, position);
    targetPosition.left += window.scrollX;
    targetPosition.top += window.scrollY;

    return targetPosition;
  }

  public static positionElementToTargetOnScreen(
    element: HTMLElement,
    target: HTMLElement,
    elementAlignment = DomElementAlignment.TopLeft,
    targetPosition = DomTargetPosition.BottomLeft
  ): DomElementPosition {
    const { left, top } = DomHandler.positionElementToTarget(element, target, targetPosition, elementAlignment);
    const elementRectangle = element.getBoundingClientRect();
    const location = DomHandler.determineOffScreenLocation(elementRectangle);

    if (location) {
      if (elementAlignment === DomElementAlignment.TopLeft && targetPosition === DomTargetPosition.BottomLeft) {
        // this is the default setting...the only places the overlay could be off screen would be right and bottom
        if (location.right && !location.bottom) {
          return DomHandler.positionElementToTarget(
            element,
            target,
            DomTargetPosition.BottomRight,
            DomElementAlignment.TopRight
          );
        } else if (location.bottom && !location.right) {
          return DomHandler.positionElementToTarget(
            element,
            target,
            DomTargetPosition.TopLeft,
            DomElementAlignment.BottomLeft
          );
        } else if (location.right && location.bottom) {
          return DomHandler.positionElementToTarget(
            element,
            target,
            DomTargetPosition.TopRight,
            DomElementAlignment.BottomRight
          );
        }
      } else if (
        elementAlignment === DomElementAlignment.TopRight &&
        targetPosition === DomTargetPosition.BottomRight
      ) {
        if (location.left && !location.bottom) {
          return DomHandler.positionElementToTarget(
            element,
            target,
            DomTargetPosition.BottomLeft,
            DomElementAlignment.TopLeft
          );
        } else if (location.bottom && !location.left) {
          return DomHandler.positionElementToTarget(
            element,
            target,
            DomTargetPosition.TopRight,
            DomElementAlignment.BottomRight
          );
        } else if (location.left && location.bottom) {
          return DomHandler.positionElementToTarget(
            element,
            target,
            DomTargetPosition.TopLeft,
            DomElementAlignment.BottomLeft
          );
        }
      }
    }

    return {
      top,
      left,
    };
  }

  public static determineOffScreenLocation(rectangle: DOMRect) {
    const windowSize = DomHandler.getScreenDimensions();
    const location: DomOffScreenLocation = {
      left: rectangle.left < 0,
      top: rectangle.top < 0,
      right: rectangle.left + rectangle.width > windowSize.width,
      bottom: rectangle.top + rectangle.height > windowSize.height,
    };

    return location.left || location.top || location.right || location.bottom ? location : undefined;
  }

  public static getScreenDimensions(): WindowSize {
    return {
      width: window.innerWidth,
      height: Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0),
    };
  }

  public static positionToScreen(
    element: HTMLElement,
    position: ScreenPosition = ScreenPosition.Center
  ): ElementAbsoluteLocation {
    const rectangle = element.getBoundingClientRect();
    const screen = DomHandler.getScreenDimensions();

    return this.calculateWithinContainer(rectangle, screen, position);
  }

  public static positionToContainer(
    element: HTMLElement,
    container: HTMLElement,
    position: ScreenPosition = ScreenPosition.Center
  ) {
    const rectangle = element.getBoundingClientRect();
    const containerRectangle = container.getBoundingClientRect();

    return this.calculateWithinContainer(rectangle, containerRectangle, position);
  }

  private static calculateWithinContainer(
    element: ElementSize,
    container: ElementSize,
    position: ScreenPosition = ScreenPosition.Center
  ) {
    let top = 0;
    let left = 0;

    switch (position) {
      case ScreenPosition.TopLeft:
        break;
      case ScreenPosition.Left:
        top = (container.height - element.height) / 2;
        break;
      case ScreenPosition.BottomLeft:
        top = container.height - element.height;
        break;
      case ScreenPosition.Top:
        left = (container.width - element.width) / 2;
        break;
      case ScreenPosition.Center:
        top = (container.height - element.height) / 2;
        left = (container.width - element.width) / 2;
        break;
      case ScreenPosition.Bottom:
        top = container.height - element.height;
        left = (container.width - element.width) / 2;
        break;
      case ScreenPosition.TopRight:
        left = container.width - element.width;
        break;
      case ScreenPosition.Right:
        top = (container.height - element.height) / 2;
        left = container.width - element.width;
        break;
      case ScreenPosition.BottomRight:
        top = container.height - element.height;
        left = container.width - element.width;
        break;
    }

    return {
      top: Math.floor(top),
      left: Math.floor(left),
    };
  }
}
